<script setup lang="ts">
import { computed } from 'vue'
import { useChat } from '@/composables/useChat'
import { useThreads } from '@/composables/useThreads'
import CostDisplay from './CostDisplay.vue'

defineProps<{
  sourcesAvailable?: boolean
  sourcesOpen?: boolean
}>()
const emit = defineEmits<{ 'toggle-sources': [] }>()

const { totalCost, sources } = useChat()
const { activeThreadId, threads } = useThreads()

// Show the conversation's title (its first user message). Falls back to a
// short id only when the thread isn't in the list yet.
const threadTitle = computed(() => {
  const id = activeThreadId.value
  if (!id) return 'New conversation'
  const t = threads.value.find(t => t.thread_id === id)
  return (t?.name && t.name.trim()) || `Thread ${id.slice(0, 8)}`
})
</script>

<template>
  <div class="flex items-center gap-3 px-5 py-2.5 min-h-[48px] border-b border-neutral-200 bg-white shrink-0">
    <div class="flex items-center gap-2 min-w-0">
      <h1 class="text-[14px] font-semibold text-neutral-800 truncate" :title="threadTitle">
        {{ threadTitle }}
      </h1>
    </div>

    <div class="ml-auto flex items-center gap-2">
      <div class="hidden md:block">
        <CostDisplay :cost="totalCost" />
      </div>

      <button
        v-if="sourcesAvailable"
        class="px-2 py-1 text-xs rounded border transition-colors flex items-center gap-1"
        :class="sourcesOpen
          ? 'bg-blue-600 border-blue-600 text-white'
          : 'bg-neutral-100 border-neutral-300 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700'"
        :title="`Sources (${sources.length})`"
        @click="emit('toggle-sources')"
      >
        Sources ({{ sources.length }})
      </button>
    </div>
  </div>
</template>
